import { m } from "framer-motion";
import { Button, Card, MenuItem, Select, Stack, Table, TableBody, TableCell, TableRow, Tooltip, Typography } from "@mui/material";
import { TableHeadCustom } from "../../../components/minimal/table";
import { useLocales } from "../../../locales";
import { fCurrency } from "../../../utils/minimal/formatNumber";
import { observer } from "mobx-react-lite";
import checkoutStore from "../../../stores/checkout.store";
import { useEffect } from "react";
import { CircularProgress } from "@mui/material";

const generateAmountOfAvailableTicket = (ticket, totalChosenTickets = 0) => {
    const { quantity, ticketsLeft, countTicketsAllowPerPurchase, minTicketsAllowPerPurchase } = ticket
    // if eventCapacityLeft is a number, it means that the event has a capacity limit if not, it means that the event has no limit set it to ticketsLeft
    const eventCapacityLeft = isNaN(ticket.eventCapacityLeft) ? ticketsLeft : ticket.eventCapacityLeft
    const eventCapacityLeftAfterChosenTickets = eventCapacityLeft - totalChosenTickets
    const maxTicketsAllowedToPurchase = Math.min(ticketsLeft, eventCapacityLeft, countTicketsAllowPerPurchase)
    const all = [<MenuItem key={-1} value={0} defaultValue="">-</MenuItem>]
    if (maxTicketsAllowedToPurchase && quantity) {
        for (let i = 1; i <= maxTicketsAllowedToPurchase; i++) {
            if (i < minTicketsAllowPerPurchase) continue // skip if i is less than minTicketsAllowPerPurchase
            const disabled = i > eventCapacityLeftAfterChosenTickets
            all.push(<MenuItem disabled={disabled} key={i} value={i}>{i}</MenuItem>)
        }
    }
    return all
}

export const TicketsTable = observer(({ tickets, handleChosenTicketChanged, chosenTickets, setActiveStep, hasChosenTickets, event }) => {
    const { translate } = useLocales()
    const headLabel = [
        { id: 'name', label: translate('event_page.ticket_name') },
        { id: 'amount', label: translate('event_page.ticket_amount') },
        { id: 'price', label: translate('event_page.ticket_price') },
    ]
    const showSeasonTicketDiscount = event && event.seasonTicketEventDiscount

    useEffect(() => {
        checkoutStore.init(event, tickets)
    }, [event, tickets])

    if (!checkoutStore.initialized) return <CircularProgress />
    return (
        <Card
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            variants={{
                visible: { opacity: 1, x: 0 },
                hidden: { opacity: 0, x: -80 }
            }}
            component={m.div} sx={{ textAlign: "center", p: 2, backdropFilter: "blur(20px)", backgroundColor: "rgba(255, 255, 255, 0.6)" }}>
            <Table>
                <TableHeadCustom headLabel={headLabel} />
                <TableBody>
                    {
                        tickets
                            .sort((a, b) => a.order < b.order ? -1 : 1)
                            .map((ticket, index) => {
                                return (
                                    <TableRow sx={{ height: "88px" }} key={index}>
                                        <TableCell>{ticket.name}</TableCell>
                                        <TableCell>
                                            {
                                                ticket.is_sold_out || ticket.eventCapacityLeft <= 0
                                                    ?
                                                    ` - ${translate('event_page.out_of_order')} -`
                                                    :
                                                    <Select onChange={(e) => handleChosenTicketChanged(ticket._id, e.target.value)} value={chosenTickets[ticket._id]?.amount} sx={{ width: "80px" }}>
                                                        {generateAmountOfAvailableTicket(ticket, checkoutStore.totalChosenTickets)}
                                                    </Select>
                                            }
                                        </TableCell>
                                        <TableCell>{ticket.price === 0 ? translate('inner_row.free_ticket') : fCurrency(ticket.price)}</TableCell>
                                    </TableRow>
                                )
                            })
                    }
                </TableBody>
            </Table>
            {
                tickets.length ? null :
                    <Stack sx={{ my: 2 }}>{translate('dashboard.events.no_tickets_for_sale')}</Stack>
            }
            <Stack>
                {
                    hasChosenTickets
                        ?
                        <Button size="large" variant="contained" onClick={() => setActiveStep(1)}>
                            {translate('event_page.to_order')}
                        </Button>
                        :
                        <Tooltip title={translate('event_page.please_choose_tickets')} placement="top">
                            <span>
                                <Button size="large" sx={{ width: "100%" }} disabled variant="contained">
                                    {translate('event_page.to_order')}
                                </Button>
                            </span>
                        </Tooltip>

                }
                {showSeasonTicketDiscount && <Typography variant="body2" sx={{ color: 'text.secondary', marginTop: '8px' }}>
                    {translate('event_page.season_ticket_discount_will_appear_after_choosing_tickets')}
                </Typography>}
            </Stack>
        </Card>
    )
})
